import { Chess } from 'chess.js';

const COLORS = Object.freeze({ w: 'سفید', b: 'سیاه' });
const CASTLING_REQUIREMENTS = Object.freeze({
  K: { king: 'e1', rook: 'h1', color: 'w', label: 'قلعه کوتاه سفید' },
  Q: { king: 'e1', rook: 'a1', color: 'w', label: 'قلعه بلند سفید' },
  k: { king: 'e8', rook: 'h8', color: 'b', label: 'قلعه کوتاه سیاه' },
  q: { king: 'e8', rook: 'a8', color: 'b', label: 'قلعه بلند سیاه' }
});

// Embedded educational diagrams only need a well-formed 8x8 layout. Keep this
// separate from validatePosition: games, exercises and PGN still require legality.
export function readDiagramFen(fen) {
  const source = String(fen ?? '').trim();
  const fields = source.split(/\s+/);
  const invalid = () => { throw new Error('ساختار FEN برای نمایش دیاگرام معتبر نیست.'); };
  if (source.length > 256 || fields.length !== 6 || !/^[wb]$/.test(fields[1])) invalid();
  if (!/^(-|[KQkq]{1,4})$/.test(fields[2]) || new Set(fields[2]).size !== fields[2].length
      || !/^(-|[a-h][36])$/.test(fields[3])) invalid();
  if (!/^\d+$/.test(fields[4]) || !/^\d+$/.test(fields[5])
      || Number(fields[4]) > 2147483647 || Number(fields[5]) < 1 || Number(fields[5]) > 2147483647) invalid();
  const ranks = fields[0].split('/');
  if (ranks.length !== 8) invalid();
  for (const rank of ranks) {
    if (!/^[prnbqkPRNBQK1-8]+$/.test(rank) || /[1-8]{2}/.test(rank)) invalid();
    if ([...rank].reduce((count, token) => count + (/[1-8]/.test(token) ? Number(token) : 1), 0) !== 8) invalid();
  }
  return fields.join(' ');
}

/**
 * Validates both FEN syntax and whether the position can arise from legal chess play.
 * A legal position may still be unsuitable as the start of a new game when it is
 * already checkmate, stalemate, an automatic draw, or otherwise game-over.
 */
export function validatePosition(fen) {
  const result = {
    isLegal: false,
    canStart: false,
    errors: [],
    warnings: [],
    normalizedFen: null,
    gameOverReason: null,
    game: null
  };

  const source = String(fen ?? '').trim();
  let game;
  try {
    game = new Chess(source);
  } catch (error) {
    result.errors.push(translateFenError(error?.message));
    return result;
  }

  result.game = game;
  result.normalizedFen = game.fen();

  const fields = source.split(/\s+/);
  const board = game.board().flat().filter(Boolean);
  const white = board.filter(piece => piece.color === 'w');
  const black = board.filter(piece => piece.color === 'b');

  validateMaterial(white, 'w', result.errors);
  validateMaterial(black, 'b', result.errors);
  validateCastling(game, fields[2], result.errors);
  validateEnPassant(game, fields, result.errors);
  validateKingSafety(game, board, result.errors);

  if (result.errors.length > 0) return result;

  result.isLegal = true;
  result.gameOverReason = getGameOverReason(game);
  result.canStart = !game.isGameOver();

  if (!result.canStart) {
    result.warnings.push(`این پوزیسیون قانونی است، اما بازی از قبل پایان یافته است: ${result.gameOverReason}`);
  }

  return result;
}

export function castlingRightIsPossible(pieces, right) {
  const requirement = CASTLING_REQUIREMENTS[right];
  if (!requirement) return false;
  const king = pieces?.get?.(requirement.king);
  const rook = pieces?.get?.(requirement.rook);
  return pieceMatches(king, requirement.color, 'king') && pieceMatches(rook, requirement.color, 'rook');
}

function validateMaterial(pieces, color, errors) {
  const colorName = COLORS[color];
  const counts = countPieces(pieces);

  if (pieces.length > 16) {
    errors.push(`${colorName} بیشتر از ۱۶ مهره دارد.`);
  }
  if (counts.p > 8) {
    errors.push(`${colorName} بیشتر از ۸ پیاده دارد.`);
  }

  const missingPawns = 8 - counts.p;
  const promotionsNeeded =
    Math.max(0, counts.q - 1) +
    Math.max(0, counts.r - 2) +
    Math.max(0, counts.b - 2) +
    Math.max(0, counts.n - 2);

  if (promotionsNeeded > missingPawns) {
    errors.push(`تعداد مهره‌های اضافه ${colorName} با تعداد پیاده‌های حذف‌شده سازگار نیست.`);
  }
}

function validateCastling(game, castlingField, errors) {
  if (!/^(-|K?Q?k?q?)$/.test(castlingField)) {
    errors.push('ترتیب یا مقدار حق قلعه‌رفتن معتبر نیست.');
    return;
  }
  if (castlingField === '-') return;

  for (const right of castlingField) {
    const requirement = CASTLING_REQUIREMENTS[right];
    const king = game.get(requirement.king);
    const rook = game.get(requirement.rook);
    if (!pieceMatches(king, requirement.color, 'k') || !pieceMatches(rook, requirement.color, 'r')) {
      errors.push(`${requirement.label} ثبت شده، اما شاه یا رخ در خانه اولیه خود نیست.`);
    }
  }
}

function validateEnPassant(game, fields, errors) {
  const target = fields[3];
  if (target === '-') return;

  const turn = fields[1];
  const halfMoveClock = Number(fields[4]);
  const file = target[0];
  const targetRank = Number(target[1]);
  const movedPawnSquare = `${file}${turn === 'w' ? 5 : 4}`;
  const originSquare = `${file}${turn === 'w' ? 7 : 2}`;
  const expectedPawnColor = turn === 'w' ? 'b' : 'w';
  const captureRank = turn === 'w' ? 5 : 4;
  const fileIndex = file.charCodeAt(0) - 97;

  if (game.get(target)) {
    errors.push('خانه En passant باید خالی باشد.');
  }

  const movedPawn = game.get(movedPawnSquare);
  if (!pieceMatches(movedPawn, expectedPawnColor, 'p')) {
    errors.push(`برای En passant باید پیاده ${COLORS[expectedPawnColor]} در خانه ${movedPawnSquare} باشد.`);
  }

  if (game.get(originSquare)) {
    errors.push(`خانه اولیه پیاده En passant یعنی ${originSquare} باید خالی باشد.`);
  }

  const capturingColor = turn;
  const hasAdjacentCapturer = [-1, 1]
    .map(offset => fileIndex + offset)
    .filter(index => index >= 0 && index < 8)
    .some(index => pieceMatches(game.get(`${String.fromCharCode(97 + index)}${captureRank}`), capturingColor, 'p'));

  if (!hasAdjacentCapturer) {
    errors.push('برای En passant باید حداقل یک پیاده حریف در خانه مجاور امکان گرفتن داشته باشد.');
  }

  if (halfMoveClock !== 0) {
    errors.push('وقتی En passant ثبت شده، شمارنده نیم‌حرکت باید صفر باشد.');
  }

  // chess.js already checks rank/turn consistency. This explicit guard keeps the
  // validator understandable if the dependency changes later.
  if ((turn === 'w' && targetRank !== 6) || (turn === 'b' && targetRank !== 3)) {
    errors.push('خانه En passant با نوبت حرکت سازگار نیست.');
  }
}

function validateKingSafety(game, board, errors) {
  const whiteKing = board.find(piece => piece.color === 'w' && piece.type === 'k');
  const blackKing = board.find(piece => piece.color === 'b' && piece.type === 'k');
  if (!whiteKing || !blackKing) return; // chess.js reports this first.

  const whiteAttackers = game.attackers(whiteKing.square, 'b');
  const blackAttackers = game.attackers(blackKing.square, 'w');

  if (whiteAttackers.length > 0 && blackAttackers.length > 0) {
    errors.push('هر دو شاه هم‌زمان در وضعیت کیش هستند.');
    return;
  }

  // The player who just moved (opposite the side to move) cannot have left their
  // own king in check. The side to move is allowed to be in check.
  if (game.turn() === 'w' && blackAttackers.length > 0) {
    errors.push('شاه سیاه در کیش است، اما نوبت سفید ثبت شده؛ این پوزیسیون از یک حرکت قانونی به‌وجود نمی‌آید.');
  }
  if (game.turn() === 'b' && whiteAttackers.length > 0) {
    errors.push('شاه سفید در کیش است، اما نوبت سیاه ثبت شده؛ این پوزیسیون از یک حرکت قانونی به‌وجود نمی‌آید.');
  }

  const checkedKingAttackers = game.turn() === 'w' ? whiteAttackers : blackAttackers;
  if (checkedKingAttackers.length > 2) {
    errors.push('شاهِ بازیکنِ نوبت بیش از دو مهاجم هم‌زمان دارد؛ چنین وضعیت کیشی از یک حرکت قانونی ایجاد نمی‌شود.');
  }
}

function countPieces(pieces) {
  return pieces.reduce((counts, piece) => {
    counts[piece.type] = (counts[piece.type] ?? 0) + 1;
    return counts;
  }, { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 });
}

function pieceMatches(piece, color, type) {
  if (!piece) return false;
  const actualColor = piece.color === 'white' ? 'w' : piece.color === 'black' ? 'b' : piece.color;
  const actualType = normalizeType(piece.type ?? piece.role);
  return actualColor === color && actualType === normalizeType(type);
}

function normalizeType(type) {
  return ({ pawn: 'p', knight: 'n', bishop: 'b', rook: 'r', queen: 'q', king: 'k' })[type] ?? type;
}

function getGameOverReason(game) {
  if (game.isCheckmate()) return 'کیش‌ومات';
  if (game.isStalemate()) return 'پات';
  if (game.isInsufficientMaterial()) return 'کمبود مهره برای مات';
  if (game.isThreefoldRepetition()) return 'تکرار سه‌باره';
  if (game.isDrawByFiftyMoves()) return 'قانون پنجاه حرکت';
  if (game.isDraw()) return 'تساوی';
  return 'بازی پایان یافته است';
}

function translateFenError(message) {
  const text = String(message ?? 'ساختار FEN معتبر نیست.');
  const translations = [
    [/must contain six/i, 'FEN باید دقیقاً شش بخش داشته باشد.'],
    [/missing white king/i, 'شاه سفید روی صفحه وجود ندارد.'],
    [/missing black king/i, 'شاه سیاه روی صفحه وجود ندارد.'],
    [/too many white kings/i, 'بیش از یک شاه سفید روی صفحه قرار دارد.'],
    [/too many black kings/i, 'بیش از یک شاه سیاه روی صفحه قرار دارد.'],
    [/pawns are on the edge rows/i, 'پیاده نمی‌تواند روی عرض اول یا هشتم قرار داشته باشد.'],
    [/en-passant square is invalid/i, 'خانه En passant معتبر نیست.'],
    [/illegal en-passant square/i, 'خانه En passant با نوبت حرکت سازگار نیست.'],
    [/castling availability is invalid/i, 'مقدار حق قلعه‌رفتن معتبر نیست.'],
    [/side-to-move is invalid/i, 'نوبت حرکت باید سفید یا سیاه باشد.'],
    [/piece data/i, 'چیدمان خانه‌های صفحه معتبر نیست.'],
    [/move number/i, 'شماره حرکت معتبر نیست.'],
    [/half move/i, 'شمارنده نیم‌حرکت معتبر نیست.']
  ];
  return translations.find(([pattern]) => pattern.test(text))?.[1] ?? text;
}
